/**
 * Utility functions for PagesContainer components
 * Functions for extracting and processing markup/XML children nodes
 */

/**
 * Extract children from Icons tag
 * @param {Array} children - Array of parsed XML/HTML nodes
 * @returns {Array|null} - Children of Icons tag or null if not found
 */
export const extractLookupIcon = (children) => {
  if (!children || !Array.isArray(children)) return null;

  // Find Icons tag
  const iconsNode = children.find(
    (node) => {
      if (!(node.type === "tag" && node.name)) return false;
      const nodeName = node.name.toLowerCase();
      // Support both legacy <Icons> and namespaced <scm:icons>
      return nodeName === "icons" || nodeName.endsWith(":icons");
    }
  );

  if (!iconsNode || !iconsNode.children) return null;

  // Return the children of Icons tag
  return iconsNode.children;
};

/**
 * Helper method to extract text content from a node
 * @param {Object} node - Parsed XML/HTML node
 * @returns {String} - Text content from the node
 */
export const getTextContent = (node) => {
  if (!node || !node.children) return "";
  
  return node.children
    .filter(child => child.type === "text")
    .map(child => child.data || "")
    .join("")
    .trim();
};

/**
 * Extract config attributes from config tag (nested structure)
 * @param {Array} children - Array of parsed XML/HTML nodes
 * @returns {Object|null} - Extracted config object or null if not found
 */
export const extractConfigAttributes = (children) => {
  if (!children || !Array.isArray(children)) {
    console.log('extractConfigAttributes: children is not an array', children);
    return null;
  }

  console.log('extractConfigAttributes: searching in children', children);

  // Find config tag (case-insensitive)
  const configNode = children.find(
    (node) => {
      const isTag = node.type === "tag";
      const nodeName = (node.name || "").toLowerCase();
      // Support both legacy <Config> and namespaced <scm:config>
      const nameMatches = nodeName === "config" || nodeName.endsWith(":config");
      console.log('Checking node:', { type: node.type, name: node.name, isTag, nameMatches });
      return isTag && nameMatches;
    }
  );

  if (!configNode) {
    console.log('extractConfigAttributes: Config node not found');
    return null;
  }

  console.log('extractConfigAttributes: Found config node', configNode);
  console.log('extractConfigAttributes: configNode.attribs', configNode.attribs);
  console.log('extractConfigAttributes: configNode.children', configNode.children);

  // Extract action from attributes
  const apiAction = configNode.attribs?.action || configNode.attribs?.apiAction || "";

  // Extract from nested tags
  // First, try to read flattened attributes from <scm:config>
  let searchField = configNode.attribs?.searchVar || configNode.attribs?.searchvar || "";
  let offset = configNode.attribs?.offsetVar || configNode.attribs?.offsetvar || "";
  let limit = configNode.attribs?.limitVar || configNode.attribs?.limitvar || "";
  let resultVar = "";
  let recordIdField = "";
  let defaultField = "";
  let totalRecordVar = "";
  const resultFields = [];

  if (configNode.children && Array.isArray(configNode.children)) {
    configNode.children.forEach((child) => {
      if (child.type === "tag") {
        const tagName = (child.name || "").toLowerCase();

        // Extract Search tag
        // (legacy structure: <Config><Search>search_input</Search>...</Config>)
        if (tagName === "search") {
          searchField = getTextContent(child) || "";
        }

        // Extract Pagination tag
        if (tagName === "pagination" && child.children) {
          child.children.forEach((paginationChild) => {
            if (paginationChild.type === "tag") {
              const paginationTagName = (paginationChild.name || "").toLowerCase();
              if (paginationTagName === "offset") {
                offset = getTextContent(paginationChild) || "";
              } else if (paginationTagName === "limit") {
                limit = getTextContent(paginationChild) || "";
              }
            }
          });
        }

        // Extract Result tag
        // Support both legacy <Result> and namespaced <scm:result>
        if (tagName === "result" || tagName.endsWith(":result")) {
          resultVar = child.attribs?.var || "";
          recordIdField = child.attribs?.recordId || child.attribs?.recordid || "";
          defaultField = child.attribs?.defaultField || child.attribs?.defaultfield || "";
          totalRecordVar = child.attribs?.totalRecord || child.attribs?.totalrecord || "";
          // Extract Field tags from Result - recursively find all Field tags
          console.log('Result tag found, children:', child.children);
          console.log('Result tag children length:', child.children?.length);
          
          // Helper function to recursively find all Field tags
          const extractFieldTags = (node) => {
            if (!node || !node.children || !Array.isArray(node.children)) {
              return;
            }
            
            node.children.forEach((fieldChild, index) => {
              console.log(`Processing child ${index}:`, { 
                type: fieldChild.type, 
                name: fieldChild.name, 
                attribs: fieldChild.attribs,
                hasChildren: !!fieldChild.children
              });
              
              // Check if this is a Field tag
              const fieldName = (fieldChild.name || "").toLowerCase();
              // Support both legacy <Field> and namespaced <scm:field>
              if (fieldChild.type === "tag" && fieldChild.name && (fieldName === "field" || fieldName.endsWith(":field"))) {
                const fieldData = {
                  key: fieldChild.attribs?.key || fieldChild.attribs?.Key || "",
                  label: fieldChild.attribs?.label || fieldChild.attribs?.Label || "",
                };
                console.log('Adding field to resultFields:', fieldData);
                resultFields.push(fieldData);
              }
              
              // Recursively check children (in case Field tags are nested)
              if (fieldChild.children && Array.isArray(fieldChild.children)) {
                extractFieldTags(fieldChild);
              }
            });
          };
          
          if (child.children && Array.isArray(child.children)) {
            extractFieldTags(child);
          }
          
          console.log('Total resultFields extracted:', resultFields.length, resultFields);
        }
      }
    });
    // console.log('resultFields1111',resultFields);
  }

  const extractedConfig = {
    apiAction,
    searchField,
    offset,
    limit,
    resultVar,
    defaultField,
    recordIdField,
    totalRecordVar,
    resultFields,
  };

  console.log('extractConfigAttributes: Extracted config', extractedConfig);
  return extractedConfig;
};

/**
 * Convert node children to HTML string
 * @param {Array} children - Array of parsed XML/HTML nodes
 * @returns {String} - HTML string representation of the nodes
 */
export const renderNodeChildren = (children) => {
  if (!children || !Array.isArray(children)) return "";

  return children
    .map((child) => {
      if (child.type === "text") {
        return child.data || "";
      } else if (child.type === "tag") {
        const attributes = Object.entries(child.attribs || {})
          .map(([key, value]) => `${key}="${value}"`)
          .join(" ");

        const childContent = renderNodeChildren(child.children || []);
        return `<${child.name}${attributes ? ` ${attributes}` : ""}>${childContent}</${child.name}>`;
      }
      return "";
    })
    .join("");
};

